import { Building2, MapPin, PenLine } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'

import { ClientForm } from '@components/client'
import { FormModal } from '@components/branded-modal'
import { AdasBox, AdasButton, AdasSwitch } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { getClient, updateClient, autoApplyClientRebate, toggleClientCccConnection, toggleClientAutoQuote } from '../../../api/api'
import { phoneNumberFormat, formatDate } from '../../../utils/utils'
import { DATE_FORMATS, LABEL_DISPLAY, CLIENT_TYPE_ENUM } from '../../../constants'
import { Logger } from '../../../logger'
import { useRebateInvoiceConfirm } from '../useRebateInvoiceConfirm'

const SECTION_LABEL = 'text-[12px] font-bold uppercase tracking-[0.14em] text-[#6B7280]'
const FIELD_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.1em] text-[#6B7280]'
const CARD = 'rounded-2xl border border-[#E5E9EE] bg-white shadow-sm'

export const ClientInfo = (props) => {
  const { clientId, existingClients = [] } = props
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [clientInfo, setClientInfo] = useState({})
  const [isClientEdit, setIsClientEdit] = useState(false)
  // Edit and the three switches all persist immediately, so none are offered read-only.
  const isReadOnly = useIsReadOnly()
  const [isTogglingActive, setIsTogglingActive] = useState(false)
  const [isTogglingAutoApply, setIsTogglingAutoApply] = useState(false)
  const [isTogglingCcc, setIsTogglingCcc] = useState(false)
  const [isTogglingAutoQuote, setIsTogglingAutoQuote] = useState(false)
  const { confirmRebateChange, rebateConfirmModal } = useRebateInvoiceConfirm()
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)

  const onSaveClientSuccessHandler = () => {
    fetchClientDetails()
  }

  const fetchClientDetails = useCallback(async () => {
    setLoading(true)
    try {
      const clientResponse = await getClient({ clientId })
      if (clientResponse?.status === 200) {
        setClientInfo(clientResponse.data)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { clientId } })
    } finally {
      setLoading(false)
    }
  }, [clientId])

  // Toggle the client's Active/Inactive state directly from the view screen and persist it.
  // Optimistically flips local state, then reverts if the update call fails.
  const handleToggleActive = useCallback(
    async (e) => {
      const nextActive = e.target.checked
      const previous = clientInfo
      setClientInfo((prev) => ({ ...prev, is_active: nextActive }))
      setIsTogglingActive(true)
      try {
        const response = await updateClient({ data: { ...clientInfo, is_active: nextActive } })
        if (response?.status === 200 && response.data) {
          setClientInfo(response.data)
        }
      } catch (error) {
        setClientInfo(previous)
        Logger.error({ message: error, payload: { file: 'ClientInfo', method: 'handleToggleActive', clientId } })
      } finally {
        setIsTogglingActive(false)
      }
    },
    [clientInfo, clientId]
  )

  const rebatePercentNumber = Number.parseFloat(clientInfo?.rebate_percent ?? '0')
  const hasRebate = !Number.isNaN(rebatePercentNumber) && rebatePercentNumber > 0
  const hasCccId = Boolean(String(clientInfo?.ccc_client_id ?? '').trim())

  // Auto-apply rebate/discount toggle — only meaningful when a rebate percentage is set.
  const handleAutoApplyToggle = useCallback(
    async (e) => {
      const checked = e.target.checked
      const previous = clientInfo
      setIsTogglingAutoApply(true)
      // Toggling rewrites the rebate on the client's open invoices — warn before switching.
      if (!(await confirmRebateChange(clientInfo.id, checked ? 'enable' : 'disable'))) {
        setIsTogglingAutoApply(false)
        return
      }
      setClientInfo((prev) => ({ ...prev, auto_apply_rebate: checked }))
      try {
        const normalized = Number.isNaN(rebatePercentNumber) ? 0 : Number(rebatePercentNumber.toFixed(2))
        const response = await autoApplyClientRebate({ data: { id: clientInfo.id, rebate_percent: normalized } })
        if (!(response && response.status === 200)) {
          setClientInfo(previous)
        }
      } catch (error) {
        setClientInfo(previous)
        Logger.error({ message: error, payload: { file: 'ClientInfo', method: 'handleAutoApplyToggle', clientId } })
      } finally {
        setIsTogglingAutoApply(false)
      }
    },
    [clientInfo, rebatePercentNumber, clientId, confirmRebateChange]
  )

  // Accept CCC Secure Share estimates (calibration_active, CAR-3700). Independent of the is_active flag.
  const handleCccToggle = useCallback(
    async (e) => {
      const checked = e.target.checked
      const previous = clientInfo
      setClientInfo((prev) => ({ ...prev, calibration_active: checked }))
      setIsTogglingCcc(true)
      try {
        const response = await toggleClientCccConnection({ data: { id: clientInfo.id, calibration_active: checked } })
        if (!(response && response.status === 200)) {
          setClientInfo(previous)
        }
      } catch (error) {
        setClientInfo(previous)
        Logger.error({ message: error, payload: { file: 'ClientInfo', method: 'handleCccToggle', clientId } })
      } finally {
        setIsTogglingCcc(false)
      }
    },
    [clientInfo, clientId]
  )

  // Create a quote automatically when an estimate is dropped for this client (CAR-3876).
  const handleAutoQuoteToggle = useCallback(
    async (e) => {
      const checked = e.target.checked
      const previous = clientInfo
      setClientInfo((prev) => ({ ...prev, auto_quote_enabled: checked }))
      setIsTogglingAutoQuote(true)
      try {
        const response = await toggleClientAutoQuote({ data: { id: clientInfo.id, auto_quote_enabled: checked } })
        if (!(response && response.status === 200)) {
          setClientInfo(previous)
        }
      } catch (error) {
        setClientInfo(previous)
        Logger.error({ message: error, payload: { file: 'ClientInfo', method: 'handleAutoQuoteToggle', clientId } })
      } finally {
        setIsTogglingAutoQuote(false)
      }
    },
    [clientInfo, clientId]
  )

  useEffect(() => {
    fetchClientDetails()
  }, [fetchClientDetails])

  useEffect(() => {
    if (isReadOnly) {
      clearPageAction()
      return undefined
    }
    setPageAction({
      label: 'Edit',
      icon: PenLine,
      onClick: () => setIsClientEdit(true)
    })
    return () => clearPageAction()
  }, [isReadOnly, setPageAction, clearPageAction])

  const roCount = clientInfo.vehicle_count || '—'
  const typeLabel = CLIENT_TYPE_ENUM[clientInfo.type] || '—'
  const rebateDisplay = `${Number(clientInfo?.rebate_percent) || 0}%`
  const lastModified = formatDate(clientInfo.updated_at || clientInfo.created_at, DATE_FORMATS.ISO_DATE_STRING) || '—'
  const location = [clientInfo.city, clientInfo.state].filter(Boolean).join(', ')

  const metrics = [
    { label: LABEL_DISPLAY.MONTHLY_VEHICLECOUNT_LABEL, value: roCount },
    { label: 'Rebate / Discount', value: rebateDisplay },
    { label: 'Type', value: typeLabel },
    { label: 'CCC ID', value: clientInfo.ccc_client_id || '—' }
  ]

  const settings = [
    {
      key: 'active',
      title: 'Client Active',
      description: 'When on, this client is available in Quotes, Work Orders & Invoices.',
      checked: !!clientInfo.is_active,
      onChange: handleToggleActive,
      disabled: isTogglingActive || isReadOnly,
      ariaLabel: 'Toggle client active status'
    },
    {
      key: 'autoApply',
      title: 'Auto-Apply Rebate / Discount to Invoices',
      description: 'When on, the discount is automatically applied to Total Cost on all Quotes, Work Orders & Invoices.',
      checked: !!clientInfo.auto_apply_rebate,
      onChange: handleAutoApplyToggle,
      disabled: isTogglingAutoApply || !hasRebate || isReadOnly,
      ariaLabel: 'Toggle auto-apply rebate'
    },
    {
      key: 'ccc',
      title: 'Accept CCC Secure Share Estimates',
      description: 'When on, the calibration center automatically receives Secure Share estimates and an AI Report is processed for each.',
      checked: clientInfo.calibration_active !== false,
      onChange: handleCccToggle,
      disabled: isTogglingCcc || !hasCccId || isReadOnly,
      ariaLabel: 'Toggle accept CCC Secure Share estimates'
    },
    {
      key: 'autoQuote',
      title: 'Auto-Generate Quote from Estimates',
      description:
        'When on, a quote is created automatically each time an estimate for this client is dropped in ADAS Intelligence or arrives via CCC Secure Share.',
      checked: !!clientInfo.auto_quote_enabled,
      onChange: handleAutoQuoteToggle,
      disabled: isTogglingAutoQuote || isReadOnly,
      ariaLabel: 'Toggle auto-generate quote from estimates'
    }
  ]

  const detailFields = [
    { label: 'City', value: clientInfo.city },
    { label: 'State', value: clientInfo.state },
    { label: 'Zip', value: clientInfo.zip },
    { label: 'Country', value: clientInfo.country },
    {
      label: 'Phone',
      value: clientInfo.phone ? (
        <a href={`tel:${clientInfo.phone}`} className='hover:underline'>
          {phoneNumberFormat(clientInfo.phone)}
        </a>
      ) : null
    },
    {
      label: 'Email',
      value: clientInfo.email ? (
        <a href={`mailto:${clientInfo.email}`} className='break-words hover:underline'>
          {clientInfo.email}
        </a>
      ) : null
    }
  ]

  return (
    <AdasBox className='space-y-7 py-4'>
      {/* Name + metrics card */}
      <AdasBox className={`${CARD} p-5`}>
        {/* Name + status + Edit (desktop only; mobile uses the global toolbar) */}
        <AdasBox className='hidden min-[435px]:flex items-start justify-between gap-4'>
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2.5'>
              <h2 className='text-[24px] font-bold leading-tight text-[#0A1628]'>{clientInfo.name}</h2>
              <span
                className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                  clientInfo.is_active ? 'bg-[#DCFCE7] text-[#16803F]' : 'bg-[#EBEDF0] text-[#6B7280]'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${clientInfo.is_active ? 'bg-[#16803F]' : 'bg-[#6B7280]'}`} />
                {clientInfo.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {location && (
              <div className='mt-1 flex items-center gap-1.5 text-[13px] text-[#6B7280]'>
                <MapPin className='h-3.5 w-3.5 flex-none' />
                <span>{location}</span>
              </div>
            )}
          </div>
          <div className='flex flex-none flex-col items-end gap-1.5'>
            {!isReadOnly && (
              <AdasButton
                startIcon={<PenLine />}
                variant='contained'
                color='primary'
                aria-label='Edit client'
                onClick={() => setIsClientEdit(true)}
                className='rounded-lg bg-[#0A1628] px-4 text-white hover:bg-[#1F2D3D]'
              >
                Edit
              </AdasButton>
            )}
            <span className='text-[12px] text-[#6B7280]'>Modified {lastModified}</span>
          </div>
        </AdasBox>

        {/* Key metrics */}
        <AdasBox className='grid grid-cols-2 gap-4 min-[435px]:mt-5 min-[435px]:grid-cols-4'>
          {metrics.map(({ label, value }) => (
            <div key={label} className='rounded-xl bg-[#F8FAFC] p-3 text-center'>
              <div className={FIELD_LABEL}>{label}</div>
              <div className='mt-1 text-[16px] font-bold text-[#0A1628]'>{value}</div>
            </div>
          ))}
        </AdasBox>
      </AdasBox>

      {/* Client settings */}
      <AdasBox className='space-y-3'>
        <div className={SECTION_LABEL}>Client Settings</div>
        <div className={`${CARD} divide-y divide-[#EBEDF0]`}>
          {settings.map(({ key, title, description, checked, onChange, disabled, ariaLabel }) => (
            <div key={key} className='flex items-center gap-4 px-5 py-4'>
              <div className='min-w-0 flex-1'>
                <div className='text-[14px] font-semibold text-[#0A1628]'>{title}</div>
                <div className='mt-0.5 text-[13px] text-[#6B7280]'>{description}</div>
              </div>
              <AdasSwitch checked={checked} onChange={onChange} disabled={disabled} inputProps={{ 'aria-label': ariaLabel }} />
            </div>
          ))}
        </div>
      </AdasBox>

      {/* Details */}
      <AdasBox className='space-y-3'>
        <div className={SECTION_LABEL}>Details</div>
        <div className={`${CARD} p-5`}>
          <div className='grid grid-cols-2 gap-x-8 gap-y-5 min-[435px]:grid-cols-3'>
            <div className='col-span-2 min-[435px]:col-span-3'>
              <div className={FIELD_LABEL}>Address</div>
              <div className='mt-1 text-[14px] text-[#0A1628]'>{clientInfo.address1 || '—'}</div>
            </div>
            {detailFields.map((field) => (
              <div key={field.label}>
                <div className={FIELD_LABEL}>{field.label}</div>
                <div className='mt-1 text-[14px] text-[#0A1628]'>{field.value || '—'}</div>
              </div>
            ))}
            <div className='col-span-2 min-[435px]:col-span-3'>
              <div className={FIELD_LABEL}>Notes</div>
              <div className='mt-1 text-[14px] text-[#0A1628]'>{clientInfo.notes || '—'}</div>
            </div>
          </div>
        </div>
      </AdasBox>

      {isClientEdit && (
        <FormModal open={isClientEdit} onClose={() => setIsClientEdit(false)} title='Client Info' icon={Building2} width='max-w-[520px]'>
          <ClientForm
            clientData={clientInfo}
            close={() => setIsClientEdit(false)}
            onSaveSuccess={onSaveClientSuccessHandler}
            existingClients={existingClients.filter((name) => name !== clientInfo.name)}
          />
        </FormModal>
      )}
      {rebateConfirmModal}
    </AdasBox>
  )
}

ClientInfo.propTypes = {
  clientId: PropTypes.string,
  existingClients: PropTypes.array
}
